import { useGetUserInfo } from 'auth';
import {
  ContractNoteFileInitResponse,
  PartyNoteResponse,
  PartyResponse,
} from 'compliance-api/generated';
import dayjs from 'dayjs';
import { ChangeEvent, ReactNode, useRef, useState } from 'react';
import {
  useCompletePartyNoteFilesMutation,
  useCreatePartyNoteMutation,
  useDeletePartyNoteMutation,
  usePartyNotesQuery,
  useUpdatePartyNoteMutation,
} from '@/modules/admin-dashboard/queries/contract-management.ts';
import { Avatar, Button, ConfirmModal, SelectButtonsNew, Spinner } from 'ui';
import { ClipIcon, EditGrayIcon, PlusGrayIcon, TrashGrayIcon } from 'ui/icons';

type PartyNotesView = 'notes' | 'history';
const FILES_ONLY_NOTE_MESSAGE = '__files_only__';

export const PartyNotesTab = ({
  party,
  partyId,
  readOnly = false,
}: {
  party: PartyResponse;
  partyId: number;
  readOnly?: boolean;
}) => {
  const [view, setView] = useState<PartyNotesView>('notes');

  return (
    <div className="space-y-5">
      <SelectButtonsNew
        options={[
          { label: 'Notes', value: 'notes' },
          { label: 'History', value: 'history' },
        ]}
        value={view}
        onChange={setView}
        capitalize={false}
        classes={{
          buttonsContainer: 'min-h-[32px]',
          button: 'min-h-[28px]',
        }}
      />

      {view === 'notes' ? (
        <PartyNotesList partyId={partyId} readOnly={readOnly} />
      ) : (
        <PartyHistoryList party={party} />
      )}
    </div>
  );
};

const PartyNotesList = ({ partyId, readOnly }: { partyId: number; readOnly: boolean }) => {
  const [draft, setDraft] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [editingNoteId, setEditingNoteId] = useState<number>();
  const [editingMessage, setEditingMessage] = useState('');
  const [noteToDelete, setNoteToDelete] = useState<PartyNoteResponse>();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const draftInputRef = useRef<HTMLTextAreaElement | null>(null);
  const { avatar, first_name, last_name } = useGetUserInfo();
  const { data, isFetching } = usePartyNotesQuery({ partyId }, !partyId);
  const notes = data?.notes || [];
  const { mutateAsync: createPartyNote, isLoading: isCreating } = useCreatePartyNoteMutation();
  const { mutateAsync: updatePartyNote, isLoading: isUpdating } = useUpdatePartyNoteMutation();
  const { mutateAsync: deletePartyNote, isLoading: isDeleting } = useDeletePartyNoteMutation();
  const { mutateAsync: completePartyNoteFiles, isLoading: isCompletingFiles } =
    useCompletePartyNoteFilesMutation();
  const isSubmitting = isCreating || isUpdating || isDeleting || isCompletingFiles;

  const handleSelectFiles = (event: ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    const nextFiles = Array.from(event.target.files || []);
    if (!nextFiles.length) return;

    setSelectedFiles(currentFiles => mergeFiles(currentFiles, nextFiles));
    event.target.value = '';
    draftInputRef.current?.focus();
  };

  const handleCreateNote = async () => {
    if (readOnly) return;
    const message = draft.trim();
    if (!message && !selectedFiles.length) return;

    const response = await createPartyNote({
      partyId,
      data: {
        message: message || FILES_ONLY_NOTE_MESSAGE,
        files: selectedFiles.length ? selectedFiles.map(fileToPartyNoteFileInitItem) : undefined,
      },
    });

    if (selectedFiles.length && response.uploads?.length) {
      const storageItemIds = await uploadPartyNoteFiles(response.uploads, selectedFiles);
      await completePartyNoteFiles({
        partyId,
        noteId: response.note.id,
        data: { storage_item_ids: storageItemIds },
      });
    }

    setDraft('');
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleStartEdit = (note: PartyNoteResponse) => {
    if (readOnly) return;
    setEditingNoteId(note.id);
    setEditingMessage(note.message === FILES_ONLY_NOTE_MESSAGE ? '' : note.message);
  };

  const handleSaveEdit = async () => {
    if (readOnly) return;
    if (!editingNoteId || !editingMessage.trim()) return;

    await updatePartyNote({
      partyId,
      noteId: editingNoteId,
      data: { message: editingMessage.trim() },
    });
    setEditingNoteId(undefined);
    setEditingMessage('');
  };

  const handleDeleteNote = async () => {
    if (readOnly) return;
    if (!noteToDelete) return;

    await deletePartyNote({
      partyId,
      noteId: noteToDelete.id,
    });
    setNoteToDelete(undefined);
  };

  if (isFetching && !data) {
    return (
      <div className="flex min-h-[160px] items-center justify-center">
        <Spinner size="md" variant="primary" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex gap-3">
          <Avatar src={avatar} firstName={first_name} lastName={last_name} size="smMd" />
          <div className="flex min-h-[72px] min-w-0 flex-1 rounded border border-gray-200 px-3 py-3">
            <button
              type="button"
              className="mr-3 h-6 w-6 shrink-0 hover:opacity-80"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSubmitting || readOnly}
            >
              <img src={ClipIcon} alt="attach file" className="h-6 w-6" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              multiple
              onChange={handleSelectFiles}
              disabled={readOnly}
            />
            <textarea
              ref={draftInputRef}
              value={draft}
              onChange={event => setDraft(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void handleCreateNote();
                }
              }}
              placeholder="Type something"
              disabled={isSubmitting || readOnly}
              className="min-h-[48px] min-w-0 flex-1 resize-none text-sm leading-[22px] text-gray-900 outline-none placeholder:text-gray-400 disabled:opacity-50"
            />
            <Button
              className="ml-3 h-8 min-h-[32px] w-fit min-w-0 px-3 py-1 text-xs self-end"
              onClick={() => void handleCreateNote()}
              disabled={readOnly || isSubmitting || (!draft.trim() && !selectedFiles.length)}
              loading={isCreating}
            >
              Send
            </Button>
          </div>
        </div>

        {selectedFiles.length > 0 && (
          <div className="ml-11 flex flex-wrap gap-2 text-xs text-gray-500">
            {selectedFiles.map(file => (
              <span key={`${file.name}_${file.size}`} className="rounded bg-gray-100 px-2 py-1">
                {file.name}
              </span>
            ))}
          </div>
        )}

        {notes.map(note => {
          const authorName = getNoteAuthorName(note);
          const isEditing = editingNoteId === note.id;

          return (
            <div key={note.id} className="flex gap-3">
              <Avatar
                firstName={getFirstName(authorName)}
                lastName={getLastName(authorName)}
                size="smMd"
                src={note?.author?.avatar_url}
              />
              <div className="min-w-0 flex-1 text-sm leading-[22px]">
                <div className="flex items-baseline gap-2">
                  <span className="font-medium text-gray-900">{authorName}</span>
                  <span className="text-xs text-gray-500">{formatTime(note.created_at)}</span>
                </div>
                {isEditing ? (
                  <div className="mt-1 space-y-2">
                    <textarea
                      value={editingMessage}
                      onChange={event => setEditingMessage(event.target.value)}
                      disabled={readOnly}
                      className="min-h-[64px] w-full resize-none rounded border border-gray-200 px-3 py-2 text-sm leading-[22px] outline-none"
                    />
                    <div className="flex gap-2">
                      <Button
                        className="min-h-[32px] w-fit min-w-0 px-3 py-1 text-xs"
                        onClick={handleSaveEdit}
                        disabled={readOnly || isSubmitting}
                        loading={isUpdating}
                      >
                        Save
                      </Button>
                      <Button
                        variant="secondary"
                        className="min-h-[32px] w-fit min-w-0 px-3 py-1 text-xs"
                        onClick={() => setEditingNoteId(undefined)}
                        disabled={readOnly || isSubmitting}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {note.message !== FILES_ONLY_NOTE_MESSAGE && (
                      <div className="text-gray-900">{note.message}</div>
                    )}
                    {!!note.files?.length && (
                      <div className="mt-1 flex flex-wrap gap-2">
                        {note.files.map(file => (
                          <a
                            key={file.storage_item_id}
                            href={file.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            {file.original_file_name}
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              {!isEditing && !readOnly && (
                <div className="flex shrink-0 items-start gap-3">
                  <button
                    type="button"
                    className="hover:opacity-80"
                    onClick={() => handleStartEdit(note)}
                    aria-label="Edit note"
                  >
                    <img src={EditGrayIcon} alt="" className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    className="hover:opacity-80"
                    onClick={() => setNoteToDelete(note)}
                    aria-label="Delete note"
                  >
                    <img src={TrashGrayIcon} alt="" className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {notes.length === 0 && (
          <div className="rounded border border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
            No notes yet.
          </div>
        )}
      </div>

      {noteToDelete && (
        <ConfirmModal
          title="Delete note"
          content="Are you sure you want to delete this note?"
          open={!!noteToDelete}
          onClose={() => setNoteToDelete(undefined)}
          onConfirm={handleDeleteNote}
        />
      )}
    </>
  );
};

const PartyHistoryList = ({ party }: { party: PartyResponse }) => {
  const historyItems = [
    party.updated_at && {
      id: 'updated',
      icon: EditGrayIcon,
      text: (
        <>
          <span className="font-medium">System</span> updated this party
        </>
      ),
      date: party.updated_at,
    },
    party.created_at && {
      id: 'created',
      icon: PlusGrayIcon,
      text: (
        <>
          <span className="font-medium">System</span> created this party
        </>
      ),
      date: party.created_at,
    },
  ].filter(Boolean) as { id: string; icon: string; text: ReactNode; date: string }[];

  if (historyItems.length === 0) {
    return <div className="py-6 text-center text-sm text-gray-500">No history yet.</div>;
  }

  return (
    <div className="space-y-4">
      {historyItems.map(item => (
        <div key={item.id} className="flex gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100">
            <img src={item.icon} alt="" className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1 text-sm leading-[22px]">
            <div className="text-gray-900">{item.text}</div>
            <div className="text-xs leading-5 text-gray-500">{formatDateTime(item.date)}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

const getFirstName = (fullName: string) => fullName.split(' ')[0] || fullName;

const getLastName = (fullName: string) => fullName.split(' ').slice(1).join(' ');

const getNoteAuthorName = (note: PartyNoteResponse) => {
  const name = [note.author?.first_name, note.author?.last_name].filter(Boolean).join(' ');
  return name || note.author?.email || 'User';
};

const formatTime = (date?: string) => {
  if (!date) return '';
  return dayjs(date).format('HH:mm');
};

const formatDateTime = (date?: string) => {
  if (!date) return '';
  return dayjs(date).format('DD-MM-YYYY, HH:mm');
};

const fileToPartyNoteFileInitItem = (file: File) => ({
  file_name: file.name,
  mime_type: file.type || 'application/octet-stream',
  file_size: file.size,
});

const uploadPartyNoteFiles = async (uploads: ContractNoteFileInitResponse[], files: File[]) => {
  const uploadedStorageItemIds = await Promise.all(
    uploads.map(async (upload, index) => {
      const file = files[index];
      if (!file) return undefined;

      const formData = new FormData();
      Object.entries(upload.presigned_upload_fields || {}).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      const response = await fetch(upload.presigned_upload_url, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload ${file.name}`);
      }

      return upload.storage_item_id;
    }),
  );

  return uploadedStorageItemIds.filter(Boolean) as string[];
};

const mergeFiles = (currentFiles: File[], nextFiles: File[]) => {
  const files = [...currentFiles];

  nextFiles.forEach(file => {
    const isDuplicate = files.some(
      existing =>
        existing.name === file.name &&
        existing.size === file.size &&
        existing.lastModified === file.lastModified,
    );

    if (!isDuplicate) {
      files.push(file);
    }
  });

  return files;
};
